import { computed } from 'vue'

import { TypeCampaign } from '@/services/campaign/enums/type-campaign.enum'
import { useAuthStore } from '@/stores/useAuthStore'
import { useCampaignRegistrationStore } from '@/stores/useCampaignRegistrationStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'

// Una sola petición en vuelo para toda la app: AppHeader y AppSidebarMobile
// están montados a la vez (se ocultan por CSS, ver DashboardLayout) y ambos
// piden el estado al montar.
let inFlightRefresh: Promise<void> | null = null
const previousCampaignIdsBySession = new Map<string, Set<string>>()
const pendingCampaignIdsBySession = new Map<string, Set<string>>()
const CAMPAIGN_IDS_STORAGE_PREFIX = 'operator_session_campaign_ids:'

const getStoredCampaignIds = (sessionIdentifier: string): Set<string> | null => {
  try {
    const raw = localStorage.getItem(`${CAMPAIGN_IDS_STORAGE_PREFIX}${sessionIdentifier}`)
    if (!raw) return null
    const ids = JSON.parse(raw)
    return Array.isArray(ids) ? new Set(ids.map(String)) : null
  } catch {
    return null
  }
}

const saveCampaignIds = (sessionIdentifier: string, ids: Set<string>) => {
  try {
    localStorage.setItem(
      `${CAMPAIGN_IDS_STORAGE_PREFIX}${sessionIdentifier}`,
      JSON.stringify([...ids]),
    )
  } catch {
    // La validación funciona también si el navegador bloquea localStorage.
  }
}

/**
 * Sincroniza el registro de campañas de CHAT contra campaign_logins.
 *
 * El canal de voz no pasa por acá: lo gobierna el heartbeat de
 * QueueMemberGrouped en useCampaignRegistrationStore. Este composable existe
 * porque un agente de solo chat no es miembro de ninguna cola de Asterisk, así
 * que su registro solo se puede confirmar preguntándole al backend.
 */
export const useCampaignRegistrationSync = () => {
  const authStore = useAuthStore()
  const campaignRegistrationStore = useCampaignRegistrationStore()
  const workspace = useWorkspaceStore()

  /**
   * El registro de voz se perdió (Asterisk dejó de reportar al agente) pero el
   * de chat sigue en pie, así que `isRegisteredInCampaign` sigue en true y el
   * botón "Iniciar campaña" no aparece. Sin este aviso el agente dejaría de
   * recibir llamadas sin enterarse: la unión de canales esconde la caída de uno.
   *
   * Exige `isVoiceStateKnown` para no dispararse en la ventana de arranque, en
   * la que todavía no llegó el primer QueueMemberGrouped, y se calla mientras
   * hay un registro pendiente de confirmar (recién pulsado el botón).
   */
  const isVoiceRegistrationLost = computed(
    () =>
      workspace.hasVoiceCampaign &&
      Boolean(authStore.userExtension) &&
      campaignRegistrationStore.isVoiceStateKnown &&
      !campaignRegistrationStore.isRegisteredInVoice &&
      !campaignRegistrationStore.isVoiceRegistrationPending &&
      campaignRegistrationStore.isRegisteredInChat,
  )

  /**
   * El espejo del anterior: el registro de chat no quedó (el backend registra
   * CALL y luego WHATSAPP, así que un fallo en el segundo deja la voz dentro y
   * el chat fuera) pero la voz sí está, con lo que `isRegisteredInCampaign`
   * sigue en true y el agente no recibiría chats sin enterarse.
   *
   * Exige `isChatStateKnown` para no avisar de algo que todavía no se pudo
   * comprobar: si la consulta a session-status falla, el estado no se marca.
   */
  const isChatRegistrationLost = computed(
    () =>
      workspace.hasWhatsAppCampaign &&
      campaignRegistrationStore.isChatStateKnown &&
      !campaignRegistrationStore.isRegisteredInChat &&
      campaignRegistrationStore.isRegisteredInVoice,
  )

  const getSessionIdentifier = () =>
    localStorage.getItem('oper_session_identifier') ||
    authStore.currentUser?.email ||
    authStore.userAlias ||
    ''

  const fetchChatRegistration = async (force: boolean) => {
    const sessionIdentifier = getSessionIdentifier()
    if (!sessionIdentifier) {
      campaignRegistrationStore.setChatRegistered(false)
      return
    }

    campaignRegistrationStore.setChatStatusLoading(true)
    try {
      // La respuesta no se cachea: cada refresh consulta el endpoint. Solo se
      // conserva el conjunto anterior de IDs para poder detectar altas nuevas.
      const sessionStatus = await authStore.checkSessionStatus(sessionIdentifier, { force })
      const currentCampaignIds = new Set(sessionStatus.campaigns.map((campaign) => campaign.id))
      const storedCampaignIds = getStoredCampaignIds(sessionIdentifier)
      const previousCampaignIds =
        previousCampaignIdsBySession.get(sessionIdentifier) ?? storedCampaignIds ?? new Set<string>()
      const hasPreviousCampaignIds =
        previousCampaignIdsBySession.has(sessionIdentifier) || storedCampaignIds !== null
      const isRegistered =
        sessionStatus.has_session && sessionStatus.campaigns.some((campaign) => campaign.is_logged_in)
      const assignmentChanged =
        [...currentCampaignIds].some((id) => !previousCampaignIds.has(id)) ||
        [...previousCampaignIds].some((id) => !currentCampaignIds.has(id))

      if (hasPreviousCampaignIds && isRegistered && assignmentChanged) {
        campaignRegistrationStore.markCampaignAssignmentChanged()
      }
      // Si cambió la asignación no se confirma todavía la nueva referencia:
      // el banner debe continuar visible hasta salir y registrarse de nuevo.
      if (!hasPreviousCampaignIds || !assignmentChanged) {
        previousCampaignIdsBySession.set(sessionIdentifier, currentCampaignIds)
        saveCampaignIds(sessionIdentifier, currentCampaignIds)
      } else {
        pendingCampaignIdsBySession.set(sessionIdentifier, currentCampaignIds)
      }

      // La campaña nueva debe aparecer aunque todavía no esté registrada.
      workspace.setCampaigns(
        sessionStatus.campaigns.map((campaign) => {
          const catalogCampaign = workspace.campaigns.find((item) => item.id === campaign.id)
          return {
            ...catalogCampaign,
            id: campaign.id,
            name: campaign.name,
            type: campaign.type as TypeCampaign,
            logo: catalogCampaign?.logo ?? '',
            dialer_type: catalogCampaign?.dialer_type ?? '',
            chats_count: catalogCampaign?.chats_count ?? 0,
            answered_calls_count: catalogCampaign?.answered_calls_count ?? 0,
            no_answered_calls_count: catalogCampaign?.no_answered_calls_count ?? 0,
            queue: catalogCampaign?.queue ?? null,
            queue_name: catalogCampaign?.queue_name ?? null,
            use_ai: catalogCampaign?.use_ai ?? false,
            masked_number: catalogCampaign?.masked_number ?? false,
          }
        }),
      )

      campaignRegistrationStore.setChatRegistered(
        sessionStatus.has_session &&
          sessionStatus.campaigns.some(
            (campaign) => campaign.type === TypeCampaign.WHATSAPP && campaign.is_logged_in,
          ),
      )
    } catch {
      // Si falla la consulta no inventamos estado: se conserva el último valor
      // confirmado hasta que la próxima sincronización responda.
    } finally {
      campaignRegistrationStore.setChatStatusLoading(false)
    }
  }

  /**
   * `force` por defecto: session-status ya no se cachea, pero sí se comparte la
   * petición en vuelo, y estas llamadas suelen venir justo detrás de un alta o
   * una baja — unirse a una consulta que salió antes pintaría el estado previo.
   */
  const refreshChatRegistration = (force = true): Promise<void> => {
    if (inFlightRefresh) return inFlightRefresh

    inFlightRefresh = fetchChatRegistration(force).finally(() => {
      inFlightRefresh = null
    })

    return inFlightRefresh
  }

  const confirmCampaignAssignmentChange = () => {
    const sessionIdentifier = getSessionIdentifier()
    if (!sessionIdentifier) return

    const currentIds = pendingCampaignIdsBySession.get(sessionIdentifier)
    if (currentIds) saveCampaignIds(sessionIdentifier, currentIds)
    campaignRegistrationStore.clearCampaignAssignmentChanged()
  }

  return {
    refreshChatRegistration,
    isVoiceRegistrationLost,
    isChatRegistrationLost,
    confirmCampaignAssignmentChange,
  }
}
